import { ParamsType } from "@ant-design/pro-provider";
import ProTable, { ActionType, ProColumns } from "@ant-design/pro-table";
import "@ant-design/pro-table/dist/table.less";
import { ProSchema } from "@ant-design/pro-utils/lib/typing";
import { RecordKey } from "@ant-design/pro-utils/lib/useEditableArray";
import { Button, Input, InputRef, Typography } from "antd";
import _ from "lodash";
import React, { RefObject, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import {
  NEW_OPERATOR_SENTINEL,
  OPERATOR_PERMISSION_SCOPES,
  Operator,
  OperatorRow,
  OperatorStatus,
  toOperatorRow,
} from "../../../../types";
import AntDeletePopupConfirm from "../../../common/AntDeletePopupConfirm";
import useSearchHotkey from "../../../hooks/useSearchHotkey";
import { FIRM_LABELS, Firm } from "@/modules/firms";
import { matchesOperator } from "@/modules/integrations/accounts";

const PERMISSION_SCOPE_VALUE_ENUM = OPERATOR_PERMISSION_SCOPES.reduce(
  (acc, scope) => ({ ...acc, [scope]: scope }),
  {} as Record<string, string>
);

const statusValueEnum = (
  text: (key: string) => string
): Record<OperatorStatus, string> => ({
  enabled: text("operators.status.enabled"),
  disabled: text("operators.status.disabled"),
});

/*
 * The status filter starts at "enabled": a disabled operator shows only when
 * the admin asks for disabled ones.
 */
const DEFAULT_FILTERS: ParamsType = { status: "enabled" };

const containsText = (value: string, search: string) =>
  value.toLowerCase().includes(search.toLowerCase());

/*
 * The "Name" filter finds an operator by any name field, the full name or the
 * email, the same way as the operator pickers ("willi depping" finds Willi
 * Depping). A new row that is not saved yet matches on its display name.
 */
const matchesName = (
  operators: Operator[],
  row: OperatorRow,
  search: string
) => {
  const operator = operators.find(({ id }) => id === row.id);
  return operator
    ? matchesOperator(search, operator)
    : containsText(row.displayName, search);
};

const filterRows = (
  rows: OperatorRow[],
  params: ParamsType,
  operators: Operator[]
) => {
  const filterPredicates: Array<((op: OperatorRow) => boolean) | undefined> = [
    params.email
      ? (d: OperatorRow) => containsText(d.email, params.email)
      : undefined,
    params.displayName
      ? (d: OperatorRow) => matchesName(operators, d, params.displayName)
      : undefined,
    params.givenNames
      ? (d: OperatorRow) => containsText(d.givenNames, params.givenNames)
      : undefined,
    params.familyName
      ? (d: OperatorRow) => containsText(d.familyName, params.familyName)
      : undefined,
    params.preferredName
      ? (d: OperatorRow) => containsText(d.preferredName, params.preferredName)
      : undefined,
    params.permissionScope
      ? (d: OperatorRow) => d.permissionScope === params.permissionScope
      : undefined,
    params.firm ? (d: OperatorRow) => d.firm === params.firm : undefined,
    params.status ? (d: OperatorRow) => d.status === params.status : undefined,
  ];

  return rows.filter((op) =>
    filterPredicates.every((predicate) => !predicate || predicate(op))
  );
};

const optionalNameColumn = (
  dataIndex: "givenNames" | "preferredName",
  title: string,
  placeholder: string
): ProColumns<OperatorRow> => ({
  dataIndex,
  title,
  valueType: "text",
  fieldProps: { placeholder },
  sorter: (a: OperatorRow, b: OperatorRow) =>
    a[dataIndex].localeCompare(b[dataIndex]),
});

const getColumns = (
  onDisableHandler: (record: OperatorRow) => Promise<void>,
  onEnableHandler: (record: OperatorRow) => Promise<void>,
  emailRef: RefObject<InputRef>,
  firmLabel: string,
  text: (key: string) => string
): ProColumns<OperatorRow>[] => [
  {
    dataIndex: "email",
    title: text("operators.columns.email"),
    valueType: "text",
    formItemProps: () => ({
      rules: [
        {
          required: true,
          message: text("operators.validation.email"),
          type: "email",
        },
      ],
    }),
    renderFormItem: (_: ProSchema<OperatorRow>, config: any) => {
      return (
        <Input
          {...config.fieldProps}
          ref={emailRef}
          placeholder="email@smarta-steuern.de"
        />
      );
    },
    editable: (_, record) => record.id === NEW_OPERATOR_SENTINEL,
    sorter: (a: OperatorRow, b: OperatorRow) => a.email.localeCompare(b.email),
  },
  {
    // The derived name the rest of the Backoffice shows. Read-only: the
    // name fields after it are the inputs it is derived from.
    dataIndex: "displayName",
    title: text("operators.columns.displayName"),
    valueType: "text",
    fieldProps: { placeholder: text("operators.enterPlaceholder") },
    editable: false,
    sorter: (a: OperatorRow, b: OperatorRow) =>
      a.displayName.localeCompare(b.displayName),
  },
  optionalNameColumn(
    "givenNames",
    text("operators.columns.givenNames"),
    text("operators.enterPlaceholder")
  ),
  {
    dataIndex: "familyName",
    title: text("operators.columns.familyName"),
    valueType: "text",
    fieldProps: { placeholder: text("operators.enterPlaceholder") },
    formItemProps: () => ({
      rules: [
        {
          required: true,
          whitespace: true,
          message: text("operators.validation.familyName"),
        },
      ],
    }),
    sorter: (a: OperatorRow, b: OperatorRow) =>
      a.familyName.localeCompare(b.familyName),
  },
  optionalNameColumn(
    "preferredName",
    text("operators.columns.preferredName"),
    text("operators.enterPlaceholder")
  ),
  {
    dataIndex: "firm",
    title: firmLabel,
    valueType: "select",
    fieldProps: { placeholder: text("operators.selectPlaceholder") },
    valueEnum: FIRM_LABELS,
    sorter: (a: OperatorRow, b: OperatorRow) => a.firm.localeCompare(b.firm),
    formItemProps: () => ({
      rules: [{ required: true, message: text("operators.validation.firm") }],
    }),
  },
  {
    dataIndex: "permissionScope",
    title: text("operators.columns.permissionScope"),
    valueType: "select",
    fieldProps: { placeholder: text("operators.selectPlaceholder") },
    valueEnum: PERMISSION_SCOPE_VALUE_ENUM,
    sorter: (a: OperatorRow, b: OperatorRow) =>
      a.permissionScope.localeCompare(b.permissionScope),
    formItemProps: () => ({
      rules: [
        {
          required: true,
          message: text("operators.validation.permissionScope"),
        },
      ],
    }),
  },
  {
    dataIndex: "status",
    title: text("operators.columns.status"),
    valueType: "select",
    fieldProps: { placeholder: text("operators.selectPlaceholder") },
    valueEnum: statusValueEnum(text),
    initialValue: DEFAULT_FILTERS.status,
    // The search form shows the field with the highest order first. The
    // status filter comes first, so it stays visible when the form collapses.
    order: 1,
    editable: false,
    sorter: (a: OperatorRow, b: OperatorRow) =>
      a.status.localeCompare(b.status),
  },
  {
    title: text("operators.columns.actions"),
    valueType: "option",
    render: (_text, record, _, action) => [
      <Typography.Link
        key="editable"
        onClick={() => {
          action?.startEditable?.(record.id);
        }}
      >
        {text("operators.edit")}
      </Typography.Link>,
      record.status === "enabled" ? (
        <AntDeletePopupConfirm
          key="disablePopup"
          title={text("operators.disableConfirm")}
          okText={text("operators.disableConfirmOk")}
          cancelText={text("operators.disableConfirmCancel")}
          onDelete={() => onDisableHandler(record)}
        >
          <Typography.Link key="disableLink">
            {text("operators.disable")}
          </Typography.Link>
        </AntDeletePopupConfirm>
      ) : (
        <Typography.Link
          key="enableLink"
          onClick={() => onEnableHandler(record)}
        >
          {text("operators.enable")}
        </Typography.Link>
      ),
    ],
  },
];

const PermissionsTable = ({
  operators,
  loading,
  onDisable,
  onEnable,
  onSave,
  defaultFirm = "smarta",
}: {
  operators: Array<Operator>;
  loading: boolean;
  onDisable: (operator: OperatorRow) => Promise<void>;
  onEnable: (operator: OperatorRow) => Promise<void>;
  onSave: (operator: OperatorRow) => Promise<void>;
  defaultFirm?: Firm;
}) => {
  const { t } = useTranslation("Operators");
  const { t: tCommon } = useTranslation();
  // String() keeps the text typed as a string for the antd props.
  const text = (key: string) => String(t(key));
  // Keep editableKeys a stable array — never undefined. ProTable's
  // useEditableArray feeds this controlled prop through rc-util useMergedState,
  // which on a defined→undefined transition syncs its inner value to undefined
  // and then hits an unguarded `editableKeys.map(...)` during render (SMT-969:
  // "Cannot read properties of undefined (reading 'map')"). Initialising to `[]`
  // and resetting to `[]` (not undefined) keeps the prop a stable array.
  const [editableKeys, setEditableRowKeys] = useState<React.Key[]>([]);
  const [dataSource, setDataSource] = useState<OperatorRow[]>([]);
  const [filters, setFilters] = useState<ParamsType>(DEFAULT_FILTERS);
  const rows = useMemo(() => operators.map(toOperatorRow), [operators]);
  const emailRef = useRef<InputRef>(null);
  useSearchHotkey(emailRef);
  const actionRef = useRef<ActionType>();

  /*
   * The filters stay applied when the operators change, so a row that is
   * disabled leaves the default "enabled" view at once.
   */
  useEffect(() => {
    setDataSource(filterRows(rows, filters, operators));
  }, [rows, filters, operators]);

  const onCancelHandler = async () => {
    const newData = _.filter(
      dataSource,
      (op) => op.id !== NEW_OPERATOR_SENTINEL
    );
    if (editableKeys?.length) {
      actionRef.current?.cancelEditable(editableKeys[0]);
    }
    setDataSource(newData);
    setEditableRowKeys([]);
  };

  const onCreateHandler = () => {
    const draft: OperatorRow = {
      id: NEW_OPERATOR_SENTINEL,
      displayName: "",
      email: "",
      givenNames: "",
      familyName: "",
      preferredName: "",
      permissionScope: "ADMIN",
      firm: defaultFirm,
      status: "enabled",
    };
    actionRef.current?.addEditRecord(draft, { position: "top" });
  };

  const onDisableHandler = async (record: OperatorRow) => {
    setEditableRowKeys([]);
    await onDisable(record);
  };

  const onEnableHandler = async (record: OperatorRow) => {
    setEditableRowKeys([]);
    await onEnable(record);
  };

  const onFilterHandler = (params: ParamsType) => {
    setFilters(params);
  };

  const onResetFilterHandler = () => {
    setFilters(DEFAULT_FILTERS);
  };

  const onSaveHandler = async (key: RecordKey, record: OperatorRow) => {
    const item = dataSource.find((op) => op.id === key);
    const next = { ...item, ...record } as OperatorRow;
    // Persist upstream and let the parent refresh `operators` (which drives
    // `dataSource` via the effect). ProTable exits edit mode once this
    // resolves and keeps the row editable if it rejects — so we must NOT
    // mutate `dataSource`/`editableKeys` here, or the editable row desyncs
    // from ProTable and the post-save re-render throws.
    await onSave(next);
  };

  const onUpdateHandler = (keys: React.Key[]) => {
    setEditableRowKeys(keys);
  };

  return (
    <ProTable<OperatorRow>
      loading={loading}
      rowKey="id"
      actionRef={actionRef}
      columns={getColumns(
        onDisableHandler,
        onEnableHandler,
        emailRef,
        String(tCommon("firm")),
        text
      )}
      dataSource={dataSource}
      headerTitle={text("operators.title")}
      toolBarRender={() => [
        <Button
          key="button"
          type="primary"
          style={{ float: "right" }}
          disabled={!!editableKeys?.length}
          onClick={onCreateHandler}
        >
          {text("operators.addNew")}
        </Button>,
      ]}
      search={{
        filterType: "query",
        labelWidth: "auto",
        searchText: text("operators.search"),
        resetText: text("operators.reset"),
        // Show every filter field at once, with no "Expand" link.
        defaultCollapsed: false,
        collapseRender: false,
      }}
      locale={{ emptyText: text("operators.emptyText") }}
      onSubmit={onFilterHandler}
      onReset={onResetFilterHandler}
      onChange={onCancelHandler}
      cardBordered={true}
      editable={{
        type: "single",
        editableKeys,
        onlyAddOneLineAlertMessage: text("operators.editOneRow"),
        onlyOneLineEditorAlertMessage: text("operators.editOneRow"),
        saveText: text("operators.save"),
        cancelText: text("operators.cancel"),
        onCancel: onCancelHandler,
        onChange: onUpdateHandler,
        onSave: onSaveHandler,
        actionRender: (_row, _config, defaultDoms) => {
          return [defaultDoms.save, defaultDoms.cancel];
        },
      }}
      toolbar={{ settings: [] }}
      pagination={{ pageSize: 10, showSizeChanger: false }}
    ></ProTable>
  );
};

export default PermissionsTable;
